/**
 * Shopfinder — Store Locator Map
 *
 * Google Maps JavaScript API + Shopfinder App Proxy API
 * Uses AdvancedMarkerElement + PinElement (google.maps.Marker deprecated since feb 2024)
 * Uses PlaceAutocompleteElement (new API since march 2025)
 */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────────────
  // Cambiar a false cuando la app este en Azure
  const MOCK_MODE = false;

  const COUNTRY_CENTERS = {
    ES: { lat: 40.4168, lng: -3.7038, zoom: 6 },
    FR: { lat: 48.8534, lng: 2.3488, zoom: 6 },
    DE: { lat: 52.52, lng: 13.405, zoom: 6 },
    IT: { lat: 41.9028, lng: 12.4964, zoom: 6 },
    US: { lat: 37.0902, lng: -95.7129, zoom: 4 },
    GB: { lat: 52.3555, lng: -1.1743, zoom: 6 },
    PT: { lat: 39.3999, lng: -8.2245, zoom: 7 },
    BE: { lat: 50.5039, lng: 4.4699, zoom: 7 },
    NL: { lat: 52.1326, lng: 5.2913, zoom: 7 },
    CA: { lat: 56.1304, lng: -106.3468, zoom: 4 },
    GR: { lat: 39.0742, lng: 21.8243, zoom: 7 },
  };

  // ── State ────────────────────────────────────────────────────────────────

  let map;
  let markers = [];
  let clusterer;
  let infoWindow;
  let storesCache = {};

  // ── Init ──────────────────────────────────────────────────────────────────
  // Espera a que google.maps este disponible (sin depender de callback en la URL)

  function init() {
    const mapEl = document.getElementById('ff-shopfinder-map');
    if (!mapEl) return;

    const visitorCountry = mapEl.dataset.country || 'ES';
    const mapId = mapEl.dataset.mapId || '';

    const mapOptions = {
      center: { lat: 41.3851, lng: 2.1734 },
      zoom: 12,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
    };
    // mapId solo si tiene valor — sin el, AdvancedMarkerElement no funciona
    // pero el mapa carga sin errores
    if (mapId) mapOptions.mapId = mapId;

    map = new google.maps.Map(mapEl, mapOptions);

    infoWindow = new google.maps.InfoWindow();
    clusterer = new markerClusterer.MarkerClusterer({
      map,
      renderer: { render: renderCluster },
    });

    map.addListener('idle', loadStores);

    initSearch();
    initNearMe();
  }

  // Espera a que Google Maps cargue, luego inicializa
  function waitForGoogleMaps() {
    if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
      init();
    } else {
      // Google Maps aun no ha cargado — reintentar
      setTimeout(waitForGoogleMaps, 100);
    }
  }

  // Arrancar cuando el DOM este listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGoogleMaps);
  } else {
    waitForGoogleMaps();
  }

  // ── Load stores by viewport ──────────────────────────────────────────────

  async function loadStores() {
    const bounds = map.getBounds();
    if (!bounds) return;

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    // Validar que el bounds es un rectángulo real
    if (Math.abs(ne.lat() - sw.lat()) < 0.001 || Math.abs(ne.lng() - sw.lng()) < 0.001) {
      return;
    }

    const bbox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()].join(',');

    try {
      const features = MOCK_MODE
        ? getMockStores()
        : await fetch(`/apps/api/shopfinder/stores?bbox=${bbox}`).then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json();
          });

      renderMarkers(features);
    } catch (err) {
      console.error('[Shopfinder] Error loading stores:', err);
    }
  }

  // ── Render markers ───────────────────────────────────────────────────────
  // Usa AdvancedMarkerElement si hay mapId, google.maps.Marker si no

  function renderMarkers(features) {
    clusterer.clearMarkers();
    markers.forEach((m) => {
      if (m.setMap) m.setMap(null);  // classic Marker
      else m.map = null;             // AdvancedMarkerElement
    });
    markers = [];

    const useAdvanced = map.getMapCapabilities?.()?.isAdvancedMarkersAvailable
      || (google.maps.marker && map.mapId);

    const newMarkers = features.map((feature) => {
      const lng = parseFloat(feature.geometry.coordinates[0]);
      const lat = parseFloat(feature.geometry.coordinates[1]);
      const { hash } = feature.properties;
      const width = 26;
      const height = 39;

      let marker;

      if (useAdvanced) {
        const { AdvancedMarkerElement } = google.maps.marker;
        const content = document.createElement('div');
        content.innerHTML = buildPinSvg(width, height);
        marker = new AdvancedMarkerElement({
          position: { lat, lng },
          content,
        });
        marker.addListener('gmp-click', () => onMarkerClick(hash, marker));
      } else {
        marker = new google.maps.Marker({
          position: { lat, lng },
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildPinSvg(width, height))}`,
            scaledSize: new google.maps.Size(width, height),
            anchor: new google.maps.Point(width / 2, height),
          },
        });
        marker.addListener('click', () => onMarkerClick(hash, marker));
      }

      markers.push(marker);
      return marker;
    });

    clusterer.addMarkers(newMarkers);
  }

  function buildPinSvg(width, height) {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="${width}" height="${height}"><path fill="#1a1a1a" d="M12 0C5.37 0 0 5.37 0 12c0 9 12 24 12 24s12-15 12-24c0-6.63-5.37-12-12-12z"/><circle cx="12" cy="12" r="4.5" fill="#ffffff"/></svg>`;
  }

  function renderCluster({ count, position }) {
    const size = Math.min(56, 30 + count * 0.8);
    const half = size / 2;
    const ringR = half - 2.5;
    const innerR = half - 5;
    const fontSize = Math.round(size * 0.32);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}"><circle cx="${half}" cy="${half}" r="${ringR}" fill="none" stroke="#000000" stroke-opacity="0.5" stroke-width="5"/><circle cx="${half}" cy="${half}" r="${innerR}" fill="#1a1a1a"/><text x="${half}" y="${half}" text-anchor="middle" dominant-baseline="central" fill="#ffffff" font-size="${fontSize}" font-weight="600" font-family="Arial, sans-serif">${count}</text></svg>`;

    const useAdvanced = map.getMapCapabilities?.()?.isAdvancedMarkersAvailable
      || (google.maps.marker && map.mapId);

    if (useAdvanced) {
      const div = document.createElement('div');
      div.innerHTML = svg;
      return new google.maps.marker.AdvancedMarkerElement({
        position,
        content: div,
      });
    }
    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(half, half),
      },
    });
  }

  // ── Store detail (fetch + cache) ──────────────────────────────────────────

  async function getStoreDetail(hash) {
    if (storesCache[hash]) return storesCache[hash];

    const store = MOCK_MODE
      ? getMockStoreDetail(hash)
      : await fetch(`/apps/api/shopfinder/stores/${encodeURIComponent(hash)}`).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        });

    if (store) storesCache[hash] = store;
    return store;
  }

  // ── Marker click → store detail ──────────────────────────────────────────

  async function onMarkerClick(hash, marker) {
    try {
      const store = await getStoreDetail(hash);
      if (!store) return;

      infoWindow.setContent(`
        <div class="ff-shopfinder-popup">
          <p class="ff-shopfinder-popup__name">${escapeHTML(store.name)}</p>
          <p class="ff-shopfinder-popup__address">
            ${escapeHTML(store.street)}<br>
            ${escapeHTML(store.zip)} ${escapeHTML(store.city)}<br>
            ${escapeHTML(store.country)}
          </p>
        </div>
      `);
      infoWindow.open(map, marker);
    } catch (err) {
      console.error('[Shopfinder] Error loading store detail:', err);
    }
  }

  // ── Search (PlaceAutocompleteElement) ────────────────────────────────────

  function initSearch() {
    const container = document.getElementById('ff-shopfinder-search');
    if (!container) return;

    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
      types: ['geocode'],
    });

    // Suppress Google's default placeholder — we render our own floating label
    placeAutocomplete.setAttribute('placeholder', ' ');

    container.appendChild(placeAutocomplete);

    // Floating label state: add --has-value class when input has text
    const hasValueClass = 'ff-shopfinder__search-container--has-value';
    function updateHasValue() {
      let value = '';
      try {
        const input = placeAutocomplete.shadowRoot && placeAutocomplete.shadowRoot.querySelector('input');
        value = (input && input.value) || '';
      } catch (e) { /* closed shadow root */ }
      container.classList.toggle(hasValueClass, value.trim().length > 0);
    }
    placeAutocomplete.addEventListener('input', updateHasValue);

    // Clear input on blur so label returns to rest state
    placeAutocomplete.addEventListener('focusout', () => {
      setTimeout(() => {
        if (container.contains(document.activeElement)) return;
        try {
          placeAutocomplete.value = '';
          const input = placeAutocomplete.shadowRoot && placeAutocomplete.shadowRoot.querySelector('input');
          if (input) {
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        } catch (e) { /* closed shadow root */ }
        container.classList.remove(hasValueClass);
      }, 200);
    });

    placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ['location', 'viewport', 'types'] });

      if (place.viewport) {
        map.fitBounds(place.viewport);
      } else if (place.location) {
        const type = place.types?.[0];
        const zoom = type === 'country' ? 5
          : type === 'postal_code' ? 13
          : 11;
        map.panTo(place.location);
        map.setZoom(zoom);
      }
      // 'idle' fires loadStores() automatically after pan/zoom
      setTimeout(updateHasValue, 0);
    });
  }

  // ── Near me button ───────────────────────────────────────────────────────

  function initNearMe() {
    const btn = document.getElementById('ff-shopfinder-near-me');
    if (!btn || !navigator.geolocation) {
      if (btn) btn.style.display = 'none';
      return;
    }

    btn.addEventListener('click', () => {
      btn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          map.panTo({ lat: coords.latitude, lng: coords.longitude });
          map.setZoom(12);
          btn.disabled = false;
        },
        () => {
          console.warn('[Shopfinder] Geolocation permission denied');
          btn.disabled = false;
        }
      );
    });
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ── Mock data ────────────────────────────────────────────────────────────

  function getMockStores() {
    return [
      // Espana — Madrid
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['-3.7029', '40.4254'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwMQ' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['-3.6918', '40.4189'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwMg' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['-3.7156', '40.4073'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwMw' } },
      // Espana — Barcelona
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.1734', '41.3851'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwNA' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.1540', '41.3780'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwNQ' } },
      // Francia — Paris
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.3200', '48.8700'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwNg' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.3488', '48.8534'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwNw' } },
      // Francia — Lyon
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['4.8357', '45.7640'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwOA' } },
      // Alemania — Berlin
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['13.4050', '52.5200'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwOQ' } },
      // Italia — Milan
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['9.1900', '45.4654'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAxMA' } },
    ];
  }

  function getMockStoreDetail(hash) {
    const stores = {
      QzAwMQ: { name: 'Optica Looley Madrid Centro', street: 'Calle Gran Via 42', zip: '28013', city: 'Madrid', country: 'ES' },
      QzAwMg: { name: 'Optica Vision Clara', street: 'Calle Serrano 88', zip: '28006', city: 'Madrid', country: 'ES' },
      QzAwMw: { name: 'Optica Arganzuela', street: 'Paseo de las Delicias 15', zip: '28045', city: 'Madrid', country: 'ES' },
      QzAwNA: { name: 'Optica Barcelona Eixample', street: 'Carrer del Consell de Cent 310', zip: '08007', city: 'Barcelona', country: 'ES' },
      QzAwNQ: { name: 'Optica Gracia', street: 'Carrer de Verdi 22', zip: '08012', city: 'Barcelona', country: 'ES' },
      QzAwNg: { name: 'Optique Marais', street: '14 Rue des Francs-Bourgeois', zip: '75003', city: 'Paris', country: 'FR' },
      QzAwNw: { name: 'KRYS Opera', street: '23 Boulevard des Capucines', zip: '75002', city: 'Paris', country: 'FR' },
      QzAwOA: { name: 'Optique du Rhone', street: '5 Place Bellecour', zip: '69002', city: 'Lyon', country: 'FR' },
      QzAwOQ: { name: 'Brillen Berlin Mitte', street: 'Unter den Linden 12', zip: '10117', city: 'Berlin', country: 'DE' },
      QzAxMA: { name: 'Ottica Milano Duomo', street: 'Via Dante 8', zip: '20121', city: 'Milano', country: 'IT' },
    };
    return stores[hash] ?? null;
  }
})();
